import React, { useMemo, useEffect, useState } from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'

import { fetchMachinesBasic } from '../actions/machines'
import { fetchSales } from '../actions/sales'

import ErrorMessage from '../components/ErrorMessage'
import { Phone } from 'lucide-react'
import LoadingProgress from '../components/LoadingProgress'

// Recharts v3
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell
} from 'recharts'

// ---------- helpers ----------

function saleLocalYMD(ts) {
  if (!ts) return ''
  return formatLocalYMD(new Date(ts))
}

function formatLocalYMD(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function startOfMonthLocalYMD() {
  const now = new Date()
  return formatLocalYMD(new Date(now.getFullYear(), now.getMonth(), 1))
}

function todayLocalYMD() {
  return formatLocalYMD(new Date())
}

function getDaysInCurrentMonth() {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()
}

// Put this near your helpers
const HOURS_24 = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`)

function computeHourlyRevenueFromSales(sales = []) {
  // prefill every hour with 0
  const byHour = new Map(HOURS_24.map(h => [h, 0]))

  for (const sale of sales) {
    const d = new Date(sale.TimeStamp || sale.timestamp || sale.createdAt || Date.now())
    const hour = `${String(d.getHours()).padStart(2, '0')}:00`
    const amount = Number(sale.Amount) || 0
    byHour.set(hour, (byHour.get(hour) || 0) + amount)
  }

  // return in 00:00 → 23:00 order, ensuring all hours exist
  return HOURS_24.map(h => ({
    hour: h,
    revenue: Math.round((byHour.get(h) || 0) * 100) / 100
  }))
}

function NewHomeView(props) {
  const { machines, sales, auth } = props

  if (auth.type === "PRODUCCION") {
    return (
      <LoadingProgress />
    )
  }

  // ----- state + effects MUST be before any return -----
  const [peakHourState, setPeakHourState] = useState(null)

  // Fetch current month once
  useEffect(() => {
    const from = startOfMonthLocalYMD()
    const to = todayLocalYMD()
    props.fetchMachinesBasic()
    props.fetchSales('', '', from, to, '', '', '', '')
  }, [])

  // Safe fallbacks (not hooks)
  const allSales = sales?.sales || []
  const allMachines = machines?.machines || []

  // ----- computed values (hooks, still before any early return) -----
  const todayYMD = todayLocalYMD()

  const todaySales = useMemo(
    () => allSales.filter(s => saleLocalYMD(s.TimeStamp) === todayYMD),
    [allSales, todayYMD]
  )

  const sumAmount = (arr) => arr.reduce((a, s) => a + (Number(s.Amount) || 0), 0)
  const monthAmount = useMemo(() => sumAmount(allSales), [allSales])
  const dayAmount = useMemo(() => sumAmount(todaySales), [todaySales])

  // compute once per mount
  const daysInMonth = useMemo(() => getDaysInCurrentMonth(), [])

  const totalLockersCapacity = useMemo(() => {
    let total = 0
    for (const m of allMachines) {
      if (m?.LastIndex != null) total += Math.abs(parseInt(m.LastIndex, 10) || 0)
    }
    return total || 1 // avoid div by zero
  }, [allMachines])

  // KPI 1: occupation = Σ Amount / Σ LastIndex
  const occupationMonth = useMemo(() => {
    const denom = totalLockersCapacity * daysInMonth
    return denom ? (allSales.length / denom) : 0
  }, [allSales.length, totalLockersCapacity, daysInMonth])

  const occupationDay = useMemo(
    () => (todaySales.length / totalLockersCapacity) || 0,
    [todaySales, totalLockersCapacity]
  )

  // KPI 3: payment methods 
  const donutColors = [
    '#ffffff', '#bcdafaff', '#9bccfaff', '#84c1fdff',
    '#75b2f8ff', '#509dfaff', '#2e8cf8ff', '#0574fcff', '#0e5891ff'
  ]

  // Build slices; drop any method with 0 total; if grand total is 0, return []
  const paymentData = useMemo(() => {
    const map = new Map()
    for (const s of allSales) {
      const pm = s.PaymentMethod || 'UNKNOWN'
      map.set(pm, (map.get(pm) || 0) + (Number(s.Amount) || 0))
    }
    const entries = Array.from(map.entries()).map(([name, value]) => ({ name, value: Number(value) || 0 }))
    const total = entries.reduce((a, d) => a + d.value, 0)

    if (total <= 0) return [] // nothing to show

    return entries
      .filter(d => d.value > 0)                      // ← drop zeros
      .map(d => ({ ...d, pct: (d.value / total) * 100 }))
  }, [allSales])

  // Attach colors and sort desc by value for legend order
  const paymentLegend = useMemo(() => {
    return paymentData
      .map((d, i) => ({ ...d, color: donutColors[i % donutColors.length] }))
      .sort((a, b) => b.value - a.value)
  }, [paymentData])

  // TPV & CASH percentages (of current month total)
  const { tpvPct, cashPct } = useMemo(() => {
    const findPct = (name) => {
      const e = paymentData.find(d => d.name === name)
      return e ? e.pct : 0
    }
    return { tpvPct: findPct('TPV'), cashPct: findPct('CASH') }
  }, [paymentData])

  // KPI 4: Average operation time (seconds -> mm:ss)
  const avgOpLabel = useMemo(() => {
    const times = allSales.map(s => Number(s.OperationTime) || 0)
    const sum = times.reduce((a, b) => a + b, 0)
    const avgSec = times.length ? Math.round(sum / times.length) : 0
    const mm = String(Math.floor(avgSec / 60)).padStart(2, '0')
    const ss = String(avgSec % 60).padStart(2, '0')
    return `${mm}:${ss}`
  }, [allSales])

  // Row 2 — left: hourly revenue + peak hour (derive with useMemo to avoid extra hook)
  const hourlySeries = useMemo(() => computeHourlyRevenueFromSales(allSales), [allSales])
  const peakHour = useMemo(() => {
    if (!hourlySeries.length) return null
    return hourlySeries.reduce((acc, p) => (p.revenue > (acc?.revenue ?? -Infinity) ? p : acc), null)?.hour ?? null
  }, [hourlySeries])

  // keep local state in sync if you need it elsewhere (this is optional)
  useEffect(() => { setPeakHourState(peakHour) }, [peakHour])

  // Row 2 — right: machine ranking by amount
  const machineRanking = useMemo(() => {
    const map = new Map()
    for (const s of allSales) {
      const serial = s?.Machine?.Serial || 'UNKNOWN'
      map.set(serial, (map.get(serial) || 0) + (Number(s.Amount) || 0))
    }
    return Array.from(map.entries())
      .map(([serial, total]) => ({ serial, total }))
      .sort((a, b) => b.total - a.total)
  }, [allSales])

  // Fancy title date in es-ES
  const fancyDate = new Date().toLocaleDateString('es-ES', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'
  })

  const monthNameEs = new Date().toLocaleDateString('es-ES', { month: 'long' })
  const monthLabel = monthNameEs.charAt(0).toUpperCase() + monthNameEs.slice(1)

  // ----- early returns AFTER all hooks -----
  if (machines?.error) return <ErrorMessage message={machines.error.message} />
  if (sales?.error) return <ErrorMessage message={sales.error.message} />

  const isLoading =
    !machines?.machines || machines.loading ||
    !sales?.sales || sales.loading

  if (isLoading) {
    // compute progress based on which requests have finished
    const requests = [
      { name: 'Máquinas', done: !!machines?.machines && !machines.loading },
      { name: 'Ventas', done: !!sales?.sales && !sales.loading },
    ]
    const total = requests.length
    const completed = requests.filter(r => r.done).length
    const progress = Math.round((completed / total) * 100)

    return (
      <div className="kpi-loading min-vh-100 d-flex flex-column align-items-center justify-content-center">
        <img className="kpi-loading-logo" src="/images/logo_blue.png" alt="Logo" height={56} />

        {/* progress bar */}
        <div
          className="kpi-progress"
          role="progressbar"
          aria-label="Cargando"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress}
        >
          <div
            className="kpi-progress-fill"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="kpi-progress-text">{progress}%</div>
      </div>
    )
  }

  return (
    <div className="container-fluid kpi-page notranslate" translate="no">
      <div className="d-flex align-items-baseline justify-content-between flex-wrap gap-2 mb-3">
        <h1 className="kpi-title m-0">
          EVOLUCIÓN<span className="kpi-title-date"> · {fancyDate}</span>
        </h1>
      </div>

      {/* -------- Row 1: 8 KPI boxes -------- */}
      <div className="row g-3 mb-3">

        {/* KPI 1: Total lockers */}
        <div className="col-12 col-sm-4 col-lg-2 py-2">
          <div className="kpi-card kpi-bg-dark">
            <div className="kpi-card-inner">
              <div className="kpi-split">
                <div className="kpi-split-item">
                  <div className="kpi-card-title text-start">Taquillas</div>
                </div>
              </div>
              <div className="kpi-value">{totalLockersCapacity}</div>
            </div>
          </div>
        </div>

        {/* KPI 2: Occupation (day) */}
        <div className="col-12 col-sm-4 col-lg-2 py-2">
          <div className="kpi-card kpi-bg-blue">
            <div className="kpi-card-inner">
              <div className="kpi-split">
                <div className="kpi-split-item">
                  <div className="kpi-card-title text-start">Ocupación</div>
                </div>
                <div className="kpi-split-item">
                  <div className="kpi-badge">Hoy</div>
                </div>
              </div>
              <div className="kpi-value">{(occupationDay * 100).toFixed(2)} %</div>
            </div>
          </div>
        </div>

        {/* KPI 3: Occupation (month) */}
        <div className="col-12 col-sm-4 col-lg-2 py-2">
          <div className="kpi-card kpi-bg-dark">
            <div className="kpi-card-inner">
              <div className="kpi-split">
                <div className="kpi-split-item">
                  <div className="kpi-card-title text-start">Ocupación</div>
                </div>
                <div className="kpi-split-item">
                  <div className="kpi-badge">{monthLabel}</div>
                </div>
              </div>
              <div className="kpi-value">{(occupationMonth * 100).toFixed(2)} %</div>
            </div>
          </div>
        </div>

        {/* KPI 4: Amounts (day) */}
        <div className="col-12 col-sm-4 col-lg-2 py-2">
          <div className="kpi-card kpi-bg-blue">
            <div className="kpi-card-inner">
              <div className="kpi-split">
                <div className="kpi-split-item">
                  <div className="kpi-card-title text-start">Ingresos</div>
                </div>
                <div className="kpi-split-item">
                  <div className="kpi-badge">Hoy</div>
                </div>
              </div>
              <div className="kpi-value">{dayAmount.toFixed(2)} €</div>
            </div>
          </div>
        </div>

        {/* KPI 5: Amounts (month) */}
        <div className="col-12 col-sm-4 col-lg-2 py-2">
          <div className="kpi-card kpi-bg-dark">
            <div className="kpi-card-inner">
              <div className="kpi-split">
                <div className="kpi-split-item">
                  <div className="kpi-card-title text-start">Ingresos</div>
                </div>
                <div className="kpi-split-item">
                  <div className="kpi-badge">{monthLabel}</div>
                </div>
              </div>
              <div className="kpi-value">{monthAmount.toFixed(2)} €</div>
            </div>
          </div>
        </div>

        {/* KPI 6: Avg Operation Time */}
        <div className="col-12 col-sm-4 col-lg-2 py-2">
          <div className="kpi-card kpi-bg-blue">
            <div className="kpi-card-inner">
              <div className="kpi-card-title text-start">Tiempo medio</div>
              <div className="kpi-value">{avgOpLabel}<br></br><h5>minutos</h5></div>
            </div>
          </div>
        </div>

      </div>

      {/* -------- Row 2: 2/3 + 1/3 -------- */}
      <div className="row g-3 align-items-stretch">
        {/* Left: hourly revenue chart + peak hour */}
        <div className="col-12 col-lg-8 py-2">
          <div className="kpi-section card border-0 shadow-sm kpi-card h-100">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="kpi-section-title">Ingreso por hora ({monthLabel})</div>
                {peakHour && <span className="kpi-peak">Hora pico: <strong>{peakHour}</strong></span>}
              </div>
              <div className="kpi-chart">
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={hourlySeries} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="hour"
                      ticks={HOURS_24}
                      interval={0}            // show every hour tick
                      tick={{ fontSize: 12 }}
                      tickMargin={8}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(v) => [`${v} €`, 'Ingreso']} />
                    <Legend />
                    <Line type="monotone" dataKey="revenue" name="Ingreso" stroke="#0b97ff" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Right: ranking by machine */}
        {/* Right: info + donut, matched height */}
        <div className="col-12 col-lg-4 py-2">
          <div className="kpi-section card border-0 shadow-sm kpi-card h-100 kpi-bg-dark">
            <div className="card-body d-flex flex-column">

              {/* Top mini KPIs */}
              <div className="row g-3 mb-3">
                <div className="col-12 col-lg-6">
                  <div className="kpi-card kpi-bg-white">
                    <div className="kpi-card-inner">
                      <div className="kpi-card-title text-start kpi-text-dark">TPV</div>
                      <div className="kpi-value kpi-text-dark">{tpvPct.toFixed(1)}%</div>
                    </div>
                  </div>
                </div>

                <div className="col-12 col-lg-6">
                  <div className="kpi-card kpi-bg-white">
                    <div className="kpi-card-inner">
                      <div className="kpi-card-title text-start kpi-text-blue">CASH</div>
                      <div className="kpi-value kpi-text-blue">{cashPct.toFixed(1)}%</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Donut panel fills remaining height */}
              <div className="kpi-card kpi-bg-dark">
                <div className="kpi-card-inner">
                  <div className="kpi-card-title text-start">Método de pago</div>

                  {paymentLegend.length > 0 ? (
                    <div className="kpi-donut-row">
                      <div className="kpi-donut-chart">
                        <ResponsiveContainer width="100%" height={160}>
                          <PieChart>
                            <Pie
                              data={paymentLegend}
                              dataKey="value"
                              nameKey="name"
                              innerRadius={45}
                              outerRadius={70}
                              paddingAngle={2}
                              stroke="none"
                            >
                              {paymentLegend.map((entry) => (
                                <Cell key={entry.name} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip
                              formatter={(v, _n, p) => [
                                `${Number(v).toFixed(2)} €`,
                                `${p.payload.name} (${p.payload.pct.toFixed(1)}%)`
                              ]}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>

                      <div className="kpi-donut-legend">
                        <ul>
                          {paymentLegend.map((d) => (
                            <li key={d.name} className="kpi-legend-item">
                              <span className="d-flex align-items-center gap-2">
                                <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                                  <circle cx="5" cy="5" r="5" fill={d.color} />
                                </svg>
                                <span className="kpi-legend-label"> {d.name}</span>
                              </span>
                              <span className="kpi-legend-value">{d.pct.toFixed(1)}%</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ) : (
                    <div className="kpi-donut-empty">Sin datos de ventas</div>
                  )}
                </div>

              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}

const mapStateToProps = state => ({
  machines: state.machines,
  sales: state.sales,
  auth: state.auth
})

const mapDispatchToProps = dispatch => ({
  fetchMachinesBasic: bindActionCreators(fetchMachinesBasic, dispatch),
  fetchSales: bindActionCreators(fetchSales, dispatch)
})

export default connect(mapStateToProps, mapDispatchToProps)(NewHomeView)
